import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// CSRF: read XSRF-TOKEN cookie and send it as a header on every request
api.interceptors.request.use((config) => {
  const token = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];
  if (token) {
    config.headers['x-xsrf-token'] = token;
  }
  return config;
});

// Auth
export const signup = (data: { email: string; password: string; displayName: string }) =>
  api.post('/auth/signup', data);

export const login = (data: { email: string; password: string }) =>
  api.post('/auth/login', data);

export const logout = () => api.post('/auth/logout');

export const getMe = () => api.get('/auth/me');

export const updateProfile = (data: any) => api.patch('/auth/me', data);

export const changePassword = (currentPassword: string, newPassword: string) =>
  api.patch('/auth/me/password', { currentPassword, newPassword });

// Books
export const getMyBooks = () => api.get('/books/my-library');

export const reorderBooks = (bookOrder: string[]) =>
  api.patch('/books/reorder', { bookOrder });

export const createBook = (data: { title?: string; authorName?: string; authorAge?: number; childName?: string; authorBio?: string }) =>
  api.post('/books', data);

export const migrateGuestBook = (book: any) => api.post('/books/migrate', { book });

export const getBook = (id: string) => api.get(`/books/${id}`);

export const saveBook = (id: string, data: any) => api.put(`/books/${id}`, data);

export const shareBook = (id: string, title: string) =>
  api.post(`/books/${id}/share`, { title });

export const deleteBook = (id: string) => api.delete(`/books/${id}`);

export const getSharedBook = (shareSlug: string) => api.get(`/books/shared/${shareSlug}`);

export const likeSharedBook = (shareSlug: string) => api.post(`/books/shared/${shareSlug}/like`);

export const reportSharedBook = (shareSlug: string, data: { reason: string; description?: string; email?: string }) =>
  api.post(`/books/shared/${shareSlug}/report`, data);

export const emailSharedBook = (shareSlug: string, email: string) =>
  api.post(`/books/shared/${shareSlug}/email`, { email });

// Billing
export const getPlans = () => api.get('/billing/plans');

export const createCheckout = (plan: string, billingPeriod: 'monthly' | 'yearly' = 'monthly') =>
  api.post('/billing/create-checkout', { plan, billingPeriod });

export const getPrintCostEstimate = (data: {
  bookId: string; coverType: string; quantity: number;
  shippingAddress?: any; shippingLevel?: string;
}) => api.post('/billing/print-cost-estimate', data);

export const createOrderCheckout = (data: {
  bookId: string; coverType: string; quantity: number; shippingLevel?: string;
}) => api.post('/billing/create-order-checkout', data);

export const getMyOrders = () => api.get('/billing/orders');

export const cancelSubscription = () => api.delete('/billing/subscription');

export const deleteAccount = (data: { password?: string; confirm?: boolean }) =>
  api.delete('/auth/me', { data });

export const exportUserData = () => api.get('/auth/me/export');

// Upload
export const uploadImage = async (file: File) => {
  const formData = new FormData();
  formData.append('image', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getUserUploads = () => api.get('/upload');

// Audio narration
export const uploadAudio = async (blob: Blob) => {
  const formData = new FormData();
  formData.append('audio', blob, 'recording.webm');
  return api.post('/audio', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Admin
export const getAdminAnalytics = () => api.get('/admin/analytics');

export const getGAAnalytics = (days?: number) =>
  api.get('/admin/ga-analytics', { params: { days } });

export const getAdminUsers = (page?: number, search?: string) =>
  api.get('/admin/users', { params: { page, search } });

export const updateAdminUser = (id: string, data: any) => api.patch(`/admin/users/${id}`, data);

export const getEmailTemplates = () => api.get('/admin/email-templates');

export const createEmailTemplate = (data: any) => api.post('/admin/email-templates', data);

export const updateEmailTemplate = (id: string, data: any) =>
  api.put(`/admin/email-templates/${id}`, data);

export const deleteEmailTemplate = (id: string) => api.delete(`/admin/email-templates/${id}`);

export const sendTestEmail = (id: string) => api.post(`/admin/email-templates/${id}/test`);

export const getEmailWorkflows = () => api.get('/admin/email-workflows');

export const createEmailWorkflow = (data: any) => api.post('/admin/email-workflows', data);

export const updateEmailWorkflow = (id: string, data: any) =>
  api.put(`/admin/email-workflows/${id}`, data);

export const toggleEmailWorkflow = (id: string) =>
  api.patch(`/admin/email-workflows/${id}/toggle`);

export const getEmailLogs = (params?: { page?: number; status?: string; search?: string }) =>
  api.get('/admin/email-logs', { params });

export const getAdminOrders = (page?: number) =>
  api.get('/admin/orders', { params: { page } });

export const updateAdminOrder = (id: string, data: any) =>
  api.patch(`/admin/orders/${id}`, data);

// Admin — User Details
export const getAdminUserDetails = (id: string) => api.get(`/admin/users/${id}`);

// Admin — Invites
export const getAdminInvites = (page?: number) =>
  api.get('/admin/invites', { params: { page } });

export const createAdminInvite = (data: { email: string; plan: string; role: string }) =>
  api.post('/admin/invites', data);

export const resendAdminInvite = (id: string) => api.post(`/admin/invites/${id}/resend`);

export const deleteAdminInvite = (id: string) => api.delete(`/admin/invites/${id}`);

// Admin — Support Tickets
export const getAdminSupportTickets = (params?: {
  page?: number; status?: string; search?: string;
}) => api.get('/admin/support', { params });

export const getAdminSupportTicket = (id: string) => api.get(`/admin/support/${id}`);

export const updateSupportTicketStatus = (id: string, data: { status?: string; priority?: string }) =>
  api.patch(`/admin/support/${id}`, data);

export const replySupportTicket = (id: string, message: string) =>
  api.post(`/admin/support/${id}/reply`, { message });

export const deleteSupportTicket = (id: string) => api.delete(`/admin/support/${id}`);

// Admin — Books Moderation
export const getAdminBooks = (params?: {
  page?: number; search?: string; filter?: string;
}) => api.get('/admin/books', { params });

export const moderateBook = (id: string, data: { isPublished?: boolean; isShared?: boolean; isFlagged?: boolean }) =>
  api.patch(`/admin/books/${id}`, data);

// Admin — Activity Logs
export const getAdminLogs = (params?: {
  page?: number; search?: string; activityType?: string;
}) => api.get('/admin/logs', { params });

// Admin — Platform Settings
export const getPlatformSettings = () => api.get('/admin/settings');

export const updatePlatformSettings = (data: {
  maintenanceMode?: boolean; signupEnabled?: boolean;
  maxBookPages?: number; maxUploadSizeMB?: number;
}) => api.post('/admin/settings', data);

// AI Illustration
export const generateAIIllustration = (prompt: string) =>
  api.post('/ai/generate-illustration', { prompt });

export const getAIQuota = () => api.get('/ai/quota');

export const getAIIllustrations = () => api.get('/ai/illustrations');

export const saveAIIllustration = (url: string, prompt: string) =>
  api.post('/ai/save-illustration', { url, prompt });

// Public Blog
export const getPublicBlogPosts = () => api.get('/blog');

export const getPublicBlogPost = (slug: string) => api.get(`/blog/${slug}`);

// Admin — Blog Posts
export const getAdminBlogPosts = (page?: number, search?: string) =>
  api.get('/admin/blog-posts', { params: { page, search } });

export const createAdminBlogPost = (data: any) => api.post('/admin/blog-posts', data);

export const updateAdminBlogPost = (id: string, data: any) =>
  api.put(`/admin/blog-posts/${id}`, data);

export const deleteAdminBlogPost = (id: string) => api.delete(`/admin/blog-posts/${id}`);

// Admin — Landing Pages
export const getAdminLandingPages = (page?: number, search?: string) =>
  api.get('/admin/landing-pages', { params: { page, search } });

export const getAdminLandingPage = (id: string) => api.get(`/admin/landing-pages/${id}`);

export const createAdminLandingPage = (data: any) => api.post('/admin/landing-pages', data);

export const updateAdminLandingPage = (id: string, data: any) =>
  api.put(`/admin/landing-pages/${id}`, data);

export const deleteAdminLandingPage = (id: string) => api.delete(`/admin/landing-pages/${id}`);

export const cloneAdminLandingPage = (id: string) =>
  api.post(`/admin/landing-pages/${id}/clone`);

// Admin — Landing Page Images
export const getAdminLandingPageImages = (tag?: string) =>
  api.get('/admin/landing-page-images', { params: { tag } });

export const uploadLandingPageImage = async (file: File, altText?: string, tags?: string[]) => {
  const formData = new FormData();
  formData.append('image', file);
  if (altText) formData.append('altText', altText);
  if (tags) formData.append('tags', JSON.stringify(tags));
  return api.post('/landing-pages/images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const updateLandingPageImage = (id: string, data: { altText?: string; tags?: string[] }) =>
  api.put(`/admin/landing-page-images/${id}`, data);

export const deleteLandingPageImage = (id: string) =>
  api.delete(`/admin/landing-page-images/${id}`);

// Public — Landing Pages
export const getPublicLandingPage = (slug: string) =>
  api.get('/landing-pages', { params: { slug } });

// Support (User-facing)
export const submitSupportTicket = async (data: {
  subject: string; message: string; category: string;
  rating?: string; pageUrl?: string; userAgent?: string;
  files?: File[];
}) => {
  const formData = new FormData();
  formData.append('subject', data.subject);
  formData.append('message', data.message);
  formData.append('category', data.category);
  if (data.rating) formData.append('rating', data.rating);
  if (data.pageUrl) formData.append('pageUrl', data.pageUrl);
  if (data.userAgent) formData.append('userAgent', data.userAgent);
  if (data.files) {
    data.files.forEach((file) => formData.append('attachments', file));
  }
  return api.post('/support/tickets', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Invites (Public)
export const validateInvite = (token: string) => api.get(`/invites/${token}`);

export const acceptInvite = (token: string, data: { password: string; displayName: string }) =>
  api.post(`/invites/${token}/accept`, data);

export default api;
